import {
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import {
    EzObject,
    EzBoolean,
    EzNumber,
    EzString,
    EzArray,
    EzFunction,
    EzPromise,
    EzHtml
} from '/ezlibrary/helpers/EzHelpers.js';

import {
    EzRegistrationState,
    EzElementEventName
} from '/ezlibrary/enums/EzEnumerations.js';

import { EzClass } from '/ezlibrary/EzClass.js';
import { EzEventEngine } from '/ezlibrary/ez-event-engine.js';
import { EzUx } from '/ezlibrary/ux/EzUx.js';
import { EzActiveSpinnerInfo } from '/public/webcomponents/spinner/EzActiveSpinnerInfo.js';

/**
 * @class
 * @extends {EzClass}
 * @description
 * Provides spinners to the UX
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     import { EzSpinner } from '/public/webcomponents/spinner/EzSpinner.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     globalThis.ezApi.ezclocker[EzSpinner.ezApiName] &&
 *         globalThis.ezApi.ezclocker?.[EzSpinner.ezApiName]?.ready
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 *     EzClass.ezWaitReady(
 *         EzSpinner.ezEventNames.onReady,
 *         {listening_class}.#ezRegistrator);
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Singleton Instance Reference:
 *      From within EzSpinner class: EzSpinner.ezInstance
 *      From outside EzSpinner class: globalThis.ezApi.ezclocker.ezSpinner
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzSpinner extends EzClass {
    /**
     * @static
     * @public @readonly @property
     * Gets string constant: 'No error message provided.'
     *
     * @returns {string}
     */
    static get NO_ERROR_MESSAGE_PROVIDED() {
        return 'No error message provided.';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets string constant: 'Please wait ...'
     *
     * @returns {string}
     */
    static get PLEASE_WAIT_() {
        return EzActiveSpinnerInfo.DEFAULT_SPINNER_MSG;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default spinner width constant: 172 (representing pixels)
     *
     * @returns {number}
     */
    static get DEFAULT_SPINNER_WIDTH() {
        return 172;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default spinner height constant: 172 (representing pixels)
     *
     * @returns {number}
     */
    static get DEFAULT_SPINNER_HEIGHT() {
        return 172;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default spinner assets url constant: '/public/webcomponents/spinner/assets/gear-172px.svg'
     *
     * @returns {string}
     */
    static get DEFAULT_SPINNER_ASSET_URL() {
        return '/public/webcomponents/spinner/assets/gear-172px.svg';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the default parent container id constant: 'body'
     *
     * @returns {string}
     */
    static get DEFAULT_PARENT_CONTAINER_ID() {
        return 'body';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the generic spinner id constant: 'GENERIC_SPINNER_ID'
     *
     * @returns {string}
     */
    static get GENERIC_SPINNER_ID() {
        return EzActiveSpinnerInfo.DEFAULT_SPINNER_ID;
    }

    /**
     * @static
     * @public @method
     * Builds the bar loader image HTML
     * Bar is orange/white moving left to right
     * Size: 16px x 16px
     *
     * @returns {string}
     */
    static ezBuildBarLoader() {
        return EzHtml.build`
            <img
                id="EzSnakeSpinner"
                src="/public/images/spinners/orange-bar-loader.gif"
                style="height:16px;width:16px"/>`;
    }

    /**
     * @static
     * @public @method
     * Builds the line loader image HTML
     * Bar is orange moving left to right
     * Size: 24px x 4px
     *
     * @returns {string}
     */
    static ezBuildLineLoader() {
        return EzHtml.build`
            <img
                id="EzSnakeSpinner"
                src="/public/images/spinners/orange-bar-loader.gif"
                style="width:24px;height:4px"/>`;
    }

    /**
     * @static
     * @public @method
     * Builds the small infinity spinner image HTML
     * Description: Infinity is orange with tansparent background
     * Size: 34px x 34px
     * Type: GIF
     *
     * @returns {string}
     */
    static ezBuildSmallInfinitySpinner() {
        return EzHtml.build`
            <img
                id="EzSnakeSpinner"
                src="/public/images/spinners/infinity-small.gif"
                style="width:34px;height:34px"/>`;
    }

    /**
     * @static
     * @public @method
     * Builds the line spinner html
     * Description: Spinner is orange with transparent background
     * Size: variable (default 52px)
     * Type: SVG
     *
     * @param {number} height
     * Default is: 52px
     *
     * @returns {string}
     */
    static ezBuildLineSpinner(height) {
        return EzHtml.build`
            <img
                id="EzSnakeSpinner"
                src="/public/images/spinners/line-spinner-orange.svg"
                style="height:${EzNumber.numberOrDefault(height, 52)}px"/>`;
    }

    /**
     * @static
     * @public @method
     * Builds the 'loading snake' spinner html
     * Description: Snake spinner with transparent background
     * Size: variable, default is 52px
     * Type: SVG
     *
     * @param {number} height
     * Default is: 52px
     * @param {string} color
     * Acceptable values: orange, white, black
     * Default: orange
     *
     * @returns {string}
     */
    static ezBuildSnakeSpinner(height, color) {
        let snakeSpinnerUrl = '/public/images/spinners/infinity-snake-spinner-orange.svg';

        color = EzString.stringOrEmpty(color).toLowerCase();

        if ('black' === color) {
            snakeSpinnerUrl = '/public/images/spinners/infinity-snake-spinner-black.svg';
        } else if ('white' === color) {
            snakeSpinnerUrl = '/public/images/spinners/infinity-snake-spinner-white.svg';
        }

        return EzHtml.build`
            <img
                id="EzSnakeSpinner"
                src="${snakeSpinnerUrl}"
                style="height:${EzNumber.numberOrDefault(height, 52)}px"/>`;
    }

    /**
     * @static
     * @public @method
     * Builds the infinity spinner html
     * Description: Orange infinity with transparent background
     * Size: Variable (default is 52px)
     * Type: SVG
     *
     * @param {number} height
     * Default is: 52px
     *
     * @returns {string}
     */
    static ezBuildInfinitySpinner(height) {
        return EzHtml.build`
            <img
                id="EzSnakeSpinner"
                src="/public/images/spinners/Infinity-200px.svg"
                style="height:${EzNumber.numberOrDefault(height, 52)}px"/>`;
    }

    /**
     * @static
     * @public @method
     * Builds the gear spinner html
     * Description:
     * The black or white gear spinner has short gears, narrow open center, transparent background
     * The orange gear spinner has long gears, wide open center, transparent background
     * Size: Variable (default is 52px)
     * Type: SVG
     *
     * @param {number} height
     * Default is: 52px
     * @param {string} color
     * Accepted values: black, white, orange
     *
     * @returns {string}
     */
    static ezBuildGearSpiner(height, color) {
        let shortGearSpinnerUrl = '/public/images/spinners/gear.svg';

        color = EzString.stringOrEmpty(color).toLowerCase();

        if ('black' === color) {
            shortGearSpinnerUrl = '/public/images/spinners/gear-spinner-black.svg';
        } else if ('white' === color) {
            shortGearSpinnerUrl = '/public/images/spinners/gear-spinner-white.svg';
        }

        return EzHtml.build`
            <img
                id="EzGearSpinner"
                src="${shortGearSpinnerUrl}"
                style="height:${EzNumber.numberOrDefault(height, 52)}px"/>`;
    }

    /**
     * @static
     * @public @field
     * Represents the default spinner options
     *
     * @type {object}
     */
    static get DEFAULT_OPTIONS() {
        return EzActiveSpinnerInfo.DEFAULT_SPINNER_OPTIONS;
    }

    /**
     * @private @field
     * Stores an array of references to the active spinners.
     * Default: []
     *
     * @type {array}
     */
    #activeSpinners = [];
    /**
     * @public @property @getter
     * Gets an array of references to the active spinners.
     *
     * @returns {array}
     */
    get activeSpinners() {
        return this.#activeSpinners;
    }
    /**
     * @public @property @setter
     * Sets an array of references to the active spinners.
     *
     * @param {undefined|null|array} activeSpinners
     * Default: []
     */
    set activeSpinners(activeSpinners = []) {
        this.#activeSpinners = EzArray.arrayOrEmpty(activeSpinners);
    }

    /**
     * @private @field
     * Stores the map of each spinner's unique options.id to number of start calls (without corresponding stop calls)
     * @type {object}
     * Default: {}
     */
    #startWaitCount = null;
    /**
     * @public @property @getter
     * Gets the map of each spinner's unique options.id to number of start calls (without corresponding stop calls)
     * @returns {object}
     */
    get startWaitCount() {
        if (!EzObject.isValid(this.#startWaitCount)) {
            this.#startWaitCount = {};

            this.#startWaitCount[EzSpinner.DEFAULT_OPTIONS.id] = 0;
        }

        return this.#startWaitCount;
    }
    /**
     * @public @property @setter
     * Sets the map of each spinner's unique options.id to number of start calls (without corresponding stop calls)
     * @param {undefined|null|object} startWaitCountMap
     * Default: { EzGlobalSpinner: 0 }
     */
    set startWaitCount(startWaitCountMap = null) {
        if (!EzObject.isValid(this.#startWaitCount)) {
            this.#startWaitCount = {};

            this.#startWaitCount[EzSpinner.DEFAULT_OPTIONS.id] = 0;
        }

        for (const optionId of startWaitCountMap) {
            if (EzString.hasLength(optionId)) {
                this.#startWaitCount[optionId] = startWaitCountMap[optionId];
            }
        }
    }

    /**
     * @public @field
     * Stores the number of page wait calls (without corresponding page wait stop calls)
     * @type {number}
     * Default: 0
     */
    #pageWaitCount = 0;
    /**
     * @public @property @getter
     * Gets the number of page wait calls (without corresponding page wait stop calls)
     * @returns {number}
     */
    get pageWaitCount() {
        return this.#pageWaitCount;
    }
    /**
     * @public @property @setter
     * Sets the number of page wait calls (without corresponding page wait stop calls)
     * @param {undefined|null|number} pageWaitCount
     * Default: 0
     */
    set pageWaitCount(pageWaitCount = 0) {
        if (!EzNumber.isNumber(pageWaitCount)) {
            throw new EzBadParamException(
                'pageWaitCount',
                this,
                'set pageWaitCount(pageWaitCount)');
        }

        this.#pageWaitCount = 0 <= pageWaitCount
            ? pageWaitCount
            : 0;
    }

    /**
     * @public @method
     * Initializes EzSpinner
     *
     * @returns {EzSpinner}
     */
    ezInit() {
        return EzSpinner.ezInstance;
    }

    /**
     * @public @method
     * Starts the full page wait spinner. After the spinner starts, a new Promise is created using the provided
     * functionToExecute reference. The functionToExecute call passes the two normal Promise params of resolve and reject.
     * However, a third param is also passed known as the waitDone function callback. The waitDone param is a reference to a
     * function that is called to hide the spinner after all work is complete. See example code below.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example usage #1 (without WaitDone):
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitPromise(
     *      'Please wait ...',
     *      (resolve, reject) => {
     *          // Perform some actions ..
     *          if (successful) {
     *             return resolve();
     *         } else {
     *            return reject();
     *         }
     *      });
     *  });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example usage #2 (with WaitDone):
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitPromise(
     *      'Please wait ...',
     *      (resolve, reject, waitDone) => {
     *          // Perform some actions ..
     *          if (successful) {
     *              // Stop the spinner
     *              waitDone();
     *
     *              // Do more stuff ...
     *              return resolve();
     *          } else {
     *              // Stop the spinner
     *              waitDone();
     *
     *              // Do more stuff
     *              return reject();
     *          }
     *      });
     *  });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} message
     * Wait message to display
     * @param {function} callbackFunction
     * Function called once the spinner starts to execute while waiting using format:
     *
     * @returns {Promise}
     */
    ezPageWaitPromise(message, callbackFunction) {
        if (!EzFunction.isFunction(callbackFunction)) {
            throw new EzBadParamException(
                'callbackFunction',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezPageWaitPromise);
        }

        return EzPromise.promise(
            (resolve, reject) => {
                const options = EzSpinner.DEFAULT_OPTIONS;

                options.message = EzString.stringWithLengthOrDefault(
                    message,
                    EzSpinner.DEFAULT_OPTIONS.message);

                EzSpinner.ezInstance.ezStart(options);

                let stopCalled = false;

                return callbackFunction(
                    // Resolve
                    (...args) => {
                        if (!stopCalled) {
                            EzSpinner.ezInstance.ezStop(options.id);

                            stopCalled = true;
                        }

                        return resolve(args);
                    },
                    // Reject
                    (...args) => {
                        if (!stopCalled) {
                            EzSpinner.ezInstance.ezStop(options.id);

                            stopCalled = true;
                        }

                        return reject(args);
                    },
                    // Waitdone (optional)
                    () => {
                        EzSpinner.ezInstance.ezStop(options.id);

                        stopCalled = true;
                    });
            });
    }

    /**
     * @public @method
     * Shows a spinner until the waitDone() callback is called and/OR the resolve()
     * callback is called.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example Use with waitDone:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitAsync(
     *      'Please wait ...',
     *      (resolve, waitDone) => {
     *          // Perform some actions ...
     *
     *          // Stop the spinner with a call to wait done
     *          waitDone();
     *
     *          // Finish up by calling resolve()
     *          return resolve();
     *      });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example Use with out waitDone:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitAsync(
     *      'Please wait ...',
     *      (resolve) => {
     *          // Perform some actions ...
     *
     *          // When spinner needs to stop, simply return the call to resolve
     *          return resolve();
     *      });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} message
     * @param {function} callbackFunction
     *
     * @returns {Promise.resolve}
     *
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitAsync(message, callbackFunction);
     */
    ezPageWaitResolve(message, callbackFunction) {
        return EzSpinner.ezInstance.ezPageWaitAsync(message, callbackFunction);
    }

    /**
     * @public @method
     * Shows a spinner until the waitDone() callback is called and/OR the resolve()
     * callback is called.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example Use with waitDone:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitAsync(
     *      'Please wait ...',
     *      (finished, waitDone) => {
     *          // Perform some actions ...
     *
     *          // Stop the spinner with a call to wait done
     *          waitDone();
     *
     *          // Finish up by calling resolve()
     *          return finished();
     *      });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example Use with out waitDone:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitAsync(
     *      'Please wait ...',
     *      (finished) => {
     *          // Perform some actions ...
     *
     *          // When spinner needs to stop, simply return the call to resolve
     *          return finished();
     *      });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} message
     * @param {function} callbackFunction
     *
     * @returns {Promise.resolve}
     */
    ezPageWaitAsync(message, callbackFunction) {
        if (!EzFunction.isFunction(callbackFunction)) {
            throw new EzBadParamException(
                'callbackFunction',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezPageWaitPromise);
        }

        return EzPromise.asyncAction(
            (finished) => {
                EzSpinner.ezInstance.pageWaitCount++;

                const options = EzSpinner.ezInstance.ezSanitizeOptions();

                options.message = EzString.stringWithLengthOrDefault(
                    message,
                    EzSpinner.DEFAULT_OPTIONS.message);

                EzSpinner.ezInstance.ezStart(options);

                let stopCalled = false;

                callbackFunction(
                    // Finished handler
                    (...args) => {
                        if (!stopCalled) {
                            EzSpinner.ezInstance.ezStop(options.id);

                            stopCalled = true;
                        }

                        return finished(...args);
                    },
                    // Waitdone Handler
                    () => {
                        if (!stopCalled) {
                            EzSpinner.ezInstance.ezStop(options.id);

                            stopCalled = true;
                        }
                    });
            });
    }

    /**
     * @public @method
     * Shows a spinner while the callbackFunction executes.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example use (without waitDone callback)
     *  globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitExecute(
     *      'Please wait ...',
     *      () => {
     *      // do some work here ...
     *  });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example Use (with waitDone callback):
     *  globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitExecute(
     *      'Please wait ...',
     *      (waitDone) => {
     *      // do some work here ...
     *      // Finally, call the provided waitDone() callback to close the spinner.
     *      waitDone();
     *  });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} message
     * @param {function} callbackFunction
     *
     * @returns {*}
     * Retuirns the result from the callbackFunction
     */
    ezPageWaitExecute(message, callbackFunction) {
        if (!EzFunction.isFunction(callbackFunction)) {
            throw new EzBadParamException(
                'callbackFunction',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezPageWaitExecute);
        }

        const spinnerOptions = EzSpinner.DEFAULT_OPTIONS;

        spinnerOptions.message = EzString.stringWithLengthOrDefault(
            message,
            EzSpinner.DEFAULT_OPTIONS.message);

        EzSpinner.ezInstance.ezStart(spinnerOptions);

        let stopCalled = false;

        const result = callbackFunction(
            // watiDone callback
            () => {
                if (!stopCalled) {
                    EzSpinner.ezInstance.ezStop(spinnerOptions.id);

                    stopCalled = true;
                }
            });

        if (!stopCalled) {
            EzSpinner.ezInstance.ezStop(spinnerOptions.id);
        }

        return result;
    }

    /**
     * @public @method
     * Starts the page wait spinner until a specific event is triggered. Then the spinner is hidden
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example with optional optionalPostEventCallbackFunction:
     *  globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWaitUntilEvent(
     *      'Please wait ...',
     *      'ezEventName',
     *      optionalPostEventCallbackFunction);
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example without optional optionalPostEventCallbackFunction:
     *  globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWaitUntilEvent(
     *      'Please wait ...',
     *      'ezEventName');
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} message
     * @param {string} eventName
     * @param {undefined|null|function} optionalPostEventCallbackFunction
     */
    ezStartPageWaitUntilEvent(message, eventName, optionalPostEventCallbackFunction = null) {
        if (!EzString.hasLength(eventName)) {
            throw new EzBadParamException(
                'eventName',
                this,
                this.ezStartPageWaitUntilEvent);
        }

        const spinnerOptions = EzSpinner.DEFAULT_OPTIONS;

        spinnerOptions.message = EzString.stringWithLengthOrDefault(
            message,
            EzSpinner.DEFAULT_OPTIONS.message);

        EzSpinner.ezInstance.ezStart(spinnerOptions);

        let stopCalled = false;

        globalThis.ezApi.ezclocker.ezEventEngine.ezWantEvent(
            eventName,
            EzSpinner.ezApiName,
            (ezEvent) => {
                globalThis.ezApi.ezclocker.ezEventEngine.ezUnwantEvent(
                    eventName,
                    EzSpinner.ezApiName);

                if (EzFunction.isFunction(optionalPostEventCallbackFunction)) {
                    optionalPostEventCallbackFunction(
                        ezEvent,
                        // watiDone callback
                        () => {
                            if (!stopCalled) {
                                EzSpinner.ezInstance.ezStop(spinnerOptions.id);

                                stopCalled = true;
                            }
                        });
                }

                if (!stopCalled) {
                    EzSpinner.ezInstance.ezStop(spinnerOptions.id);
                }
            });
    }

    /**
     * @public @method
     * Starts the full page wait spinner. After the spinner starts, the functionToExecute function is called (if provided).
     * The first param passed to that function (known as waitDone function callback) is a reference to a callback function
     * that you will call (after all work is complete) to stop and hide the spinner. See example code below:
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example with finished, waitDone, and thisRef:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWait(
     *      EzSpinner.PLEASE_WAIT_,
     *      (finished, waitDone) => {
     *          // Do some stuff here....
     *
     *          // Stop the spinner
     *          waitDone();
     *
     *          // Finish execute by calling resolve
     *          return resolve(myResult);
     *      },
     *      this);
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example with finished, and waitDone
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWait(
     *      EzSpinner.PLEASE_WAIT_,
     *      (finished, waitDone) => {
     *          // Do some stuff here....
     *
     *          // Stop the spinner
     *          waitDone();
     *
     *          // Finish execute by calling resolve
     *          return resolve(myResult);
     *      });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example with finished and thisRef:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWait(
     *      EzSpinner.PLEASE_WAIT_,
     *      (finished) => {
     *          // Do some stuff here....
     *
     *          // Stop the spinner and finish execution by calling resolve
     *          return resolve(myResult);
     *      },
     *      this);
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example with finished:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWait(
     *      EzSpinner.PLEASE_WAIT_,
     *      (finished) => {
     *          // Do some stuff here....
     *
     *          // Stop the spinner and finish execution by calling resolve
     *          return resolve(myResult);
     *      });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example with waitDone:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWait(
     *      EzSpinner.PLEASE_WAIT_,
     *      (waitDone) => {
     *          // Do some stuff here....
     *
     *          // Stop the spinner and finish execution by calling resolve
     *          return waitDone(myResult);
     *      });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {undefined|null|string} message
     * @param {undefined|null|function} callbackFunction
     * @param {undefined|null|object} thisRef
     *
     * @returns {Promise.resolve}
     * Promise.resolve contains the result from the call to the provided callbackFunction (if available)
     */
    ezStartPageWait(message, callbackFunction, thisRef = this) {
        if (!EzFunction.isFunction(callbackFunction)) {
            throw new EzBadParamException(
                'callbackFunction',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezStartPageWait);
        }

        return EzPromise.asyncAction(
            (finished) => {
                const spinnerOptions = EzSpinner.DEFAULT_OPTIONS;

                spinnerOptions.message = EzString.stringWithLengthOrDefault(
                    message,
                    EzSpinner.DEFAULT_OPTIONS.message);

                EzSpinner.ezInstance.pageWaitCount++;

                let stopCalled = false;

                EzSpinner.ezInstance.ezStart(spinnerOptions);

                return EzObject.isValid(thisRef)
                    ? callbackFunction.apply(
                        thisRef,
                        [
                            // finished callback
                            (...args) => {
                                if (!stopCalled) {
                                    EzSpinner.ezInstance.ezStop(spinnerOptions.id);

                                    stopCalled = true;
                                }

                                return finished.apply(thisRef, args);
                            },
                            // watiDone callback
                            () => {
                                if (!stopCalled) {
                                    EzSpinner.ezInstance.ezStop(spinnerOptions.id);

                                    stopCalled = true;
                                }
                            }
                        ])
                    : callbackFunction(
                        // finished callback
                        (...args) => {
                            if (!stopCalled) {
                                EzSpinner.ezInstance.ezStop(spinnerOptions.id);

                                stopCalled = true;
                            }

                            return finished(...args);
                        },
                        // watiDone callback
                        () => {
                            if (!stopCalled) {
                                EzSpinner.ezInstance.ezStop(spinnerOptions.id);

                                stopCalled = true;
                            }
                        });
            });
    }

    /**
     * @public @method
     * Starts the full page wait spinner. After the spinner starts, a new Promise is created using the provided
     * functionToExecute reference. The functionToExecute call passes the two normal Promise params of resolve and reject.
     * However, a third param is also passed known as the waitDone function callback. The waitDone param is a reference to a
     * function that is called to hide the spinner after all work is complete. See example code below.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example usage #1 (without WaitDone):
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWaitPromise(
     *      'Please wait ...',
     *      (resolve, reject) => {
     *          // Perform some actions ..
     *          if (successful) {
     *             return resolve();
     *         } else {
     *            return reject();
     *         }
     *      });
     *  });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example usage #2 (with WaitDone):
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWaitPromise(
     *      'Please wait ...',
     *      (resolve, reject, waitDone) => {
     *          // Perform some actions ..
     *          if (successful) {
     *              // Stop the spinner
     *              waitDone();
     *
     *              // Do more stuff ...
     *              return resolve();
     *          } else {
     *              // Stop the spinner
     *              waitDone();
     *
     *              // Do more stuff
     *              return reject();
     *          }
     *      });
     *  });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~     *
     * @param {string} message
     * Wait message to display
     * @param {function} callbackFunction
     * Function to execute while waiting
     *
     * @returns {Promise}
     *
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitPromise(message, callbackFunction)
     */
    ezStartPageWaitPromise(message, callbackFunction) {
        return EzSpinner.ezInstance.ezPageWaitPromise(
            message,
            callbackFunction);
    }

    /**
     * @public @method
     * Shows a spinner until the waitDone() callback is called and/OR the resolve()
     * callback is called.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example Use with waitDone:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWaitResolve(
     *      'Please wait ...',
     *      (resolve, waitDone) => {
     *          // Perform some actions ...
     *
     *          // Stop the spinner with a call to wait done
     *          waitDone();
     *
     *          // Finish exception by calling the resolve callback
     *          return resolve();
     *      });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example Use with out waitDone:
     *  return globalThis.ezApi.ezclocker.ezSpinner.ezStartPageWaitResolve(
     *      'Please wait ...',
     *      (resolve) => {
     *          // Perform some actions ...
     *
     *          // Stop the spinner and finish exception by calling the resolve callback
     *          return resolve();
     *      });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} message
     * @param {undefined|null|function} functionToExecute
     *
     * @returns {Promise.resolve}
     *
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitAsync(message, callbackFunction)
     */
    ezStartPageWaitResolve(message, callbackFunction) {
        return EzSpinner.ezInstance.ezPageWaitAsync(message, callbackFunction);
    }

    /**
     * @public @method
     * Shows a spinner while the callbackFunction executes.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example use (without waitDone callback)
     *  globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitExecute(
     *      'Please wait ...',
     *      () => {
     *      // do some work here ...
     *  });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example Use (with waitDone callback):
     *  globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitExecute(
     *      'Please wait ...',
     *      (waitDone) => {
     *      // do some work here ...
     *      // Finally, call the provided waitDone() callback to close the spinner.
     *      waitDone();
     *  });
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {string} message
     * @param {function} callbackFunction
     *
     * @returns {*}
     *
     * @deprecated
     * Migrate to: globalThis.ezApi.ezclocker.ezSpinner.ezPageWaitExecute(message, callbackFunction);
     */
    ezStartPageWaitExecute(message, functionToExecute) {
        return EzSpinner.ezInstance.ezPageWaitExecute(message, functionToExecute);
    }

    /**
     * @public @method
     * Stops the page wait spinners no matter how many times the ezStartPageWait() method was called.
     *
     * @returns {object}
     * Returns an object that contains the current startWaitCount and pageWaitCount:
     *  {
     *      startWaitCount: EzNumber.numberOrZero(EzSpinner.ezInstance?.startWaitCount?.length)
     *      pageWaitCount: EzNumber.numberOrZero(EzSpinner.ezInstance?.pageWaitCount),
     *      activeSpinners: EzObject.assignOrEmpty(EzSpinner.ezInstance?.activeSpinners)
     *  }
     */
    ezForceStopPageWaitSpinner() {
        return EzSpinner.ezInstance.ezUnregisterAllActiveSpinners();
    }

    /**
     * @public @method
     * Sanitizes the options to make sure all required properties are available or set to default.
     *
     * @param {undefined|null|object} options
     * Default: EzSpinner.DEFAULT_OPTIONS
     *
     * @returns {object}
     * Returns an new spinner options object defined as below:
     *  {
     *      id: <spinnerOptions.id or EzSpinner.DEFAULT_OPTIONS.id>,
     *      spinner: { // spinnerOptions.spinner or EzSpinner.DEFAULT_OPTIONS.spinner
     *          width: <spinnerOptions.spinner.width or '172'>,
     *          height: <spinnerOptions.spinner.height or '172'>,
     *          maxWidth: <spinnerOptions.spinner.maxWidth or spinner.width>,
     *          minWidth: <spinnerOptions.spinner.minWidth or spinner.width>,
     *          assetUrl: <spinnerOptions.spinner.assetUrl or EzSpinner.DEFAULT_OPTIONS.spinner.assetUrl>
     *      },
     *      spinnerContainerId: <EzSpinner.ezInstance.ezGenerateSpinnerContainerId(spinnerId)>,
     *      spinnerPageOverlayId: <EzSpinner.ezInstance.ezGenerateSpinnerPageOverlayId(spinnerId)>,
     *      spinnerLayoutContainerId: <EzSpinner.ezInstance.ezGenerateSpinnerLayoutContainerId(spinnerId)>,
     *      spinnerMessageId: <EzSpinner.ezInstance.ezGenerateSpinnerMessageId(spinnerId)>,
     *      message: <spinnerOptions.message or EzSpinner.PLEASE_WAIT_>,
     *      parentContainerId: <spinnerOptions.parentContainerId or EzSpinner.DEFAULT_OPTIONS.parentContainerId>
     *  }
     */
    ezSanitizeOptions(spinnerOptions = EzSpinner.DEFAULT_OPTIONS) {
        if (!EzObject.isValid(spinnerOptions)) {
            return EzSpinner.DEFAULT_OPTIONS;
        }

        const spinnerInstance = EzObject.isValid(spinnerOptions?.spinner)
            ? {
                ...spinnerOptions?.spinner
            }
            : {
                ...EzSpinner.DEFAULT_OPTIONS.spinner
            };

        spinnerInstance.assetUrl = EzString.hasLength(spinnerOptions?.spinner?.assetUrl)
            ? spinnerOptions.spinner.assetUrl
            : EzSpinner.DEFAULT_OPTIONS.spinner.assetUrl;

        spinnerInstance.width = EzNumber.isNumber(spinnerOptions?.spinner?.width) || EzString.hasLength(spinnerOptions?.spinner?.width)
            ? spinnerOptions.spinner.width
            : EzSpinner.DEFAULT_SPINNER_WIDTH;

        spinnerInstance.height = EzNumber.isNumber(spinnerOptions?.spinner?.height) || EzString.hasLength(spinnerOptions?.spinner?.height)
            ? spinnerOptions.spinner.height
            : EzSpinner.DEFAULT_SPINNER_HEIGHT;

        if (EzBoolean.isTrue(spinnerOptions.spinner?.canResize)) {
            spinnerInstance.maxWidth = null;

            spinnerInstance.minWidth = null;
        } else {
            spinnerInstance.maxWidth = EzNumber.isNumber(spinnerOptions?.spinner?.maxWidth) || EzString.hasLength(spinnerOptions?.spinner?.maxWidth)
                ? spinnerOptions.spinner.maxWidth
                : spinnerInstance.spinnerWidth;

            spinnerInstance.minWidth = EzNumber.isNumber(spinnerOptions?.spinner?.minWidth) || EzString.hasLength(spinnerOptions?.spinner?.minWidth)
                ? spinnerOptions.spinner.minWidth
                : spinnerInstance.spinnerWidth;
        }

        const spinnerId = EzString.stringWithLengthOrDefault(
            spinnerOptions?.id,
            EzSpinner.DEFAULT_OPTIONS.id);

        // Sanatized Spinner Options
        // TODO: Create class for sanatized spinner options
        return {
            // Default id = EzString.stringWithLengthOrDefault(EzSpinnerType.ezSpinnerConfig(EzSpinnerType.LINE_SPINNER_SVG)?.id, 'GENERIC_SPINNER_ID')
            id: spinnerId,
            spinner: spinnerInstance,
            spinnerContainerId: EzSpinner.ezInstance.ezGenerateSpinnerContainerId(spinnerId),
            spinnerPageOverlayId: EzSpinner.ezInstance.ezGenerateSpinnerPageOverlayId(spinnerId),
            spinnerLayoutContainerId: EzSpinner.ezInstance.ezGenerateSpinnerLayoutContainerId(spinnerId),
            spinnerMessageId: EzSpinner.ezInstance.ezGenerateSpinnerMessageId(spinnerId),
            // Default message = EzSpinner.PLEASE_WAIT_ = 'Please wait ...'
            message: EzString.stringWithLengthOrDefault(
                spinnerOptions?.message,
                EzSpinner.PLEASE_WAIT_),
            // Default parentContainerId = EzSpinner.DEFAULT_OPTIONS.parentContainerId = 'body'
            parentContainerId: EzString.stringWithLengthOrDefault(
                spinnerOptions?.parentContainerId,
                EzSpinner.DEFAULT_OPTIONS.parentContainerId)
        };
    }

    /**
     * @public @method
     * Gets the spinner window container's id: `EzSpinner_${spinnerId}_LayoutContainerId`
     *
     * @param {string} spinnerId
     *
     * @returns {string}
     */
    ezGenerateSpinnerLayoutContainerId(spinnerId) {
        if (!EzString.hasLength(spinnerId)) {
            throw new EzBadParamException(
                'spinnerId',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezGenerateSpinnerLayoutContainerId);
        }

        return `${EzSpinner.ezInstance.ezGenerateSpinnerContainerId(spinnerId)}_LayoutContainerId`;
    }

    /**
     * @public @method
     * Gets the spinner message container's id: `EzSpinner_${spinnerId}_Message`
     *
     * @param {string} spinnerId
     *
     * @returns {string}
     */
    ezGenerateSpinnerMessageId(spinnerId) {
        if (!EzString.hasLength(spinnerId)) {
            throw new EzBadParamException(
                'spinnerId',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezGenerateSpinnerMessageId);
        }

        return `${EzSpinner.ezInstance.ezGenerateSpinnerContainerId(spinnerId)}_Message`;
    }

    /**
     * @public @method
     * Generates a spinner's page overlay div element id: `EzSpinner_${spinnerId}_PageOverlay`
     *
     * @param {string} spinnerId
     *
     * @returns {string}
     */
    ezGenerateSpinnerPageOverlayId(spinnerId) {
        if (!EzString.hasLength(spinnerId)) {
            throw new EzBadParamException(
                'spinnerId',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezGenerateSpinnerPageOverlayId);
        }

        return `${EzSpinner.ezInstance.ezGenerateSpinnerContainerId(spinnerId)}_PageOverlay`;
    }

    /**
     * @public @method
     * Generates the spinner's HTML container id: `EzSpinner_${spinnerId}`
     *
     * @param {string} spinnerId
     *
     * @returns {string}
     */
    ezGenerateSpinnerContainerId(spinnerId) {
        if (!EzString.hasLength(spinnerId)) {
            throw new EzBadParamException(
                'spinnerId',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezGenerateSpinnerContainerId);
        }

        return `EzSpinner_${spinnerId}`;
    }

    /**
     * @public @method
     * Resizes the spinner when the container resizes
     *
     * @param {string} spinnerId
     */
    ezResizeSpinnerToWindow(spinnerId) {
        if (!EzString.hasLength(spinnerId)) {
            throw new EzBadParamException(
                'spinnerId',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezResizeSpinnerToWindow);
        }

        const activeSpinerInfo = EzSpinner.ezInstance.activeSpinners?.[spinnerId];

        if (!EzObject.isValid(activeSpinerInfo)) {
            // nothing to resize
            return;
        }

        // Adjust the sizing
        const cHeight = window.innerHeight;

        if (0 === cHeight) {
            // nothing to resize into
            return;
        }

        const sHeight = activeSpinerInfo.options.spinner.height;

        const position = {
            top: `${EzNumber.asNumber((cHeight / 2) - (sHeight / 2))}px`
        };

        EzUx.setCssProperty(
            EzSpinner.ezInstance.ezGenerateSpinnerContainerId(activeSpinerInfo.options.id),
            'width',
            window.innerWidth);

        EzUx.setCssProperty(
            EzSpinner.ezInstance.ezGenerateSpinnerLayoutContainerId(activeSpinerInfo.options.id),
            'margin-top',
            position.top);
    }

    /**
     * @public @method
     * Registers an active spinner
     *
     * @param {undefined|null|object} unsanitarySpinnerOptions
     * Default: EzSpinner.DEFAULT_OPTIONS
     */
    ezRegisterActiveSpinner(unsanitarySpinnerOptions = EzSpinner.DEFAULT_OPTIONS) {
        const sanitizedSpinnerOptions = EzObject.isValid(unsanitarySpinnerOptions)
            ? EzSpinner.ezInstance.ezSanitizeOptions(unsanitarySpinnerOptions)
            : EzSpinner.ezInstance.ezSanitizeOptions(EzSpinner.DEFAULT_OPTIONS);

        EzSpinner.ezInstance.startWaitCount[sanitizedSpinnerOptions.id]++;

        EzSpinner.ezInstance.pageWaitCount++;

        let activeSpinnerInfo = EzObject.assignOrNull(
            EzSpinner.ezInstance?.activeSpinners?.[sanitizedSpinnerOptions.id]);

        if (EzObject.isValid(activeSpinnerInfo)) {
            activeSpinnerInfo.spinnerId = sanitizedSpinnerOptions.id;
            activeSpinnerInfo.options = sanitizedSpinnerOptions;
            activeSpinnerInfo.activeCount = 1 + EzNumber.numberOrDefault(
                activeSpinnerInfo?.activeCount,
                1);

            // Spinner already visible, update the message instead:
            // Update the existing spinner UX message instead
            globalThis.ezApi.ezclocker.ezUi.ezSetContent(
                activeSpinnerInfo.options.spinnerMessageId,
                activeSpinnerInfo.options.message);

            //EzUx.removeElement(activeSpinnerInfo.options.spinnerContainerId);
        } else {
            activeSpinnerInfo = {
                spinnerId: sanitizedSpinnerOptions.id,
                options: sanitizedSpinnerOptions,
                activeCount: 1
            };

            // Create and show new spinner
            EzSpinner.ezInstance.activeSpinners[activeSpinnerInfo.spinnerId] = activeSpinnerInfo;

            //globalThis.ezApi.ezclocker.ezUi.ezPrependContent(
            EzUx.prependContent(
                activeSpinnerInfo.options.parentContainerId,
                EzSpinner.ezInstance.ezBuildSpinnerBox(activeSpinnerInfo.options));

            EzUx.show(activeSpinnerInfo.options.spinnerContainerId);
        }

        //globalThis.ezApi.ezclocker.ezUi.ezShow(activeSpinnerInfo.options.spinnerContainerId);

        EzSpinner.ezInstance.ezResizeSpinnerToWindow(activeSpinnerInfo.spinnerId);

        // Hook window resize event to make sure the spinner size stays the same
        globalThis.ezApi.ezclocker.ezEventEngine.ezWantElementEvent(
            EzString.stringOrDefault(
                activeSpinnerInfo.spinnerId,
                EzSpinner.DEFAULT_PARENT_CONTAINER_ID),
            EzElementEventName.RESIZE,
            `${EzSpinner.ezApiName}_${sanitizedSpinnerOptions.spinnerContainerId}`,
            () => EzSpinner.ezInstance.ezResizeSpinnerToWindow(activeSpinnerInfo.spinnerId));
    }

    /**
     * @public @method
     * Unregisters an active spinner (stopping the spinner)
     *
     * @param {undefined|null|string} ezSpinnerId
     * Default: null
     *
     * @returns {object}
     * Returns an object that contains the current startWaitCount and pageWaitCount:
     *  {
     *      activeSpinnerInfo: {
     *          spinnerId: {string},
     *          options: {object},
     *          activeCount: {number}
     *      };,
     *      startWaitCount: EzNumber.numberOrZero(EzSpinner.ezInstance?.startWaitCount?.length)
     *      pageWaitCount: EzNumber.numberOrZero(EzSpinner.ezInstance?.pageWaitCount),
     *      activeSpinners: EzObject.assignOrEmpty(EzSpinner.ezInstance?.activeSpinners)
     *  }
     */
    ezUnregisterActiveSpinner(spinnerId = null) {
        if (!EzString.hasLength(spinnerId)) {
            spinnerId = EzSpinner.DEFAULT_OPTIONS.id;
        }

        if (0 < EzSpinner.ezInstance.startWaitCount[spinnerId]) {
            EzSpinner.ezInstance.startWaitCount[spinnerId]--;
        } else {
            EzSpinner.ezInstance.startWaitCount[spinnerId] = 0;
        }

        if (0 < EzSpinner.ezInstance.pageWaitCount) {
            EzSpinner.ezInstance.pageWaitCount--;
        } else {
            EzSpinner.ezInstance.pageWaitCount = 0;
        }

        const activeSpinnerInstance = EzSpinner.ezInstance.activeSpinners?.[spinnerId];

        if (!EzObject.isValid(activeSpinnerInstance)) {
            // No active spinner with the id
            return;
        }

        activeSpinnerInstance.activeCount = activeSpinnerInstance.activeCount - 1;

        if (0 >= activeSpinnerInstance.activeCount) {
            // Once the spinner count reaches one, it can be closed
            const spinnerContainerId = EzSpinner.ezInstance.ezGenerateSpinnerContainerId(activeSpinnerInstance.spinnerId);

            // Remove the spinner html
            globalThis.ezApi.ezclocker.ezUi.ezRemoveElement(spinnerContainerId);
            //EzUx.removeElement(spinnerContainerId);

            delete EzSpinner.ezInstance.activeSpinners[activeSpinnerInstance.spinnerId];

            globalThis.ezApi.ezclocker.ezEventEngine.ezUnwantElementEvent(
                EzSpinner.DEFAULT_OPTIONS.id,
                EzElementEventName.RESIZE,
                `${EzSpinner.ezApiName}_${spinnerContainerId}`);
        }

        return {
            activeSpinnerInfo: activeSpinnerInstance,
            startWaitCount: EzNumber.numberOrZero(EzSpinner.ezInstance?.startWaitCount),
            pageWaitCount: EzNumber.numberOrZero(EzSpinner.ezInstance?.pageWaitCount),
            activeSpinners: EzObject.assignOrEmpty(EzSpinner.ezInstance?.activeSpinners)
        };
    }

    /**
     * @public @method
     * Builds the spinner box HTML ux using the provided options and ids.
     *
     * @param {object} sanitizedSpinnerOptions
     * Expects the spinner options to have been processed by EzSpinner.ezInstance.ezSanitizeOptions(...)
     *
     * @returns {string}
     */
    ezBuildSpinnerBox(sanitizedSpinnerOptions = null) {
        if (!EzObject.isValid(sanitizedSpinnerOptions) ||
            !EzString.hasLength(sanitizedSpinnerOptions?.spinnerContainerId) ||
            !EzString.hasLength(sanitizedSpinnerOptions?.spinnerLayoutContainerId) ||
            !EzString.hasLength(sanitizedSpinnerOptions?.spinnerMessageId) ||
            !EzObject.isValid(sanitizedSpinnerOptions?.spinner)) {
            throw new EzBadParamException(
                'sanitizedSpinnerOptions',
                EzSpinner.ezInstance,
                EzSpinner.ezInstance.ezBuildSpinnerBox);
        }

        return EzHtml.build`
            <div
                id="${sanitizedSpinnerOptions.spinnerContainerId}"
                style="position:fixed;top:0;left:0;height:100%;width:100%;background-color:rgba(80,80,80,0.3);z-index:9999;">
                <style
                    id="${sanitizedSpinnerOptions.spinnerContainerId}_Styles">
                    .ezSpinner-container {
                        top: 45%;
                        padding: 8px;
                        /* Variable --ezAlphaBackgroundWhite4 defined in ez-body.css */
                        background-color: var(--ezAlphaBackgroundWhite4);
                        /* Variable --ezClockerBlack defined in ez-body.css */
                        color: var(--ezClockerBlack);
                        font-size: 24pt;
                        text-align: center;
                        box-shadow: var(--ezBlackThickBottomShadow);
                        opacity: 1;
                    }
                    .ezSpinner-message {
                        margin: 0px;
                        padding: 0px;
                        /* Variable --ezClockerBlack defined in ez-body.css */
                        color: var(--ezClockerBlack);
                        font-size: 24pt;
                        text-align: center;
                    }
                    .ezSpinner-spinner-img {
                        margin: 0px;
                        padding: 0px;
                        max-width: ${sanitizedSpinnerOptions.spinner.maxWidth}px;
                        min-width: ${sanitizedSpinnerOptions.spinner.minWidth}px;
                        width: ${sanitizedSpinnerOptions.spinner.width}px;
                        height: ${sanitizedSpinnerOptions.spinner.height}px;
                        text-align: center;
                        vertical-align: middle;
                    }
                </style>
                <div
                    id="${sanitizedSpinnerOptions.spinnerLayoutContainerId}_BubbleShadow"
                    style="background-color:transparent;border-radius:30%;box-shadow:0px 0px 30px 0px rgba(255, 156, 0, 0.3);">
                    <div
                        id="${sanitizedSpinnerOptions.spinnerLayoutContainerId}"
                        class="ezSpinner-container animate__animated animate__pulse">
                        <img
                            id="${sanitizedSpinnerOptions.spinnerLayoutContainerId}_Image"
                            class="ezSpinner-spinner-img"
                            align="center"
                            src="${sanitizedSpinnerOptions.spinner.assetUrl}"/>
                        <span
                            id="${sanitizedSpinnerOptions.spinnerMessageId}"
                            class="ezSpinner-message">
                            ${EzString.stringWithLengthOrDefault(sanitizedSpinnerOptions.message, EzSpinner.PLEASE_WAIT_)}
                        </span>
                    </div>
                </div>
            </div>`;
    }

    /**
     * @public @method
     * Unregisters and stops all active spinners
     *
     * @returns {object}
     * Returns an object that contains the current startWaitCount and pageWaitCount:
     *  {
     *      startWaitCount: EzNumber.numberOrZero(EzSpinner.ezInstance?.startWaitCount?.length)
     *      pageWaitCount: EzNumber.numberOrZero(EzSpinner.ezInstance?.pageWaitCount),
     *      activeSpinners: EzObject.assignOrEmpty(EzSpinner.ezInstance?.activeSpinners)
     *  }
     */
    ezUnregisterAllActiveSpinners() {
        if (EzArray.hasLength(EzSpinner.ezInstance?.activeSpinners)) {
            for (const activeSpinnerInfo of EzSpinner.ezInstance.activeSpinners) {
                if (EzObject.isValid(activeSpinnerInfo)) {
                    const spinnerContainerId = EzSpinner.ezInstance.ezGenerateSpinnerContainerId(activeSpinnerInfo.spinnerId);

                    globalThis.ezApi.ezclocker.ezEventEngine.ezUnwantElementEvent(
                        EzSpinner.DEFAULT_PARENT_CONTAINER_ID,
                        EzElementEventName.RESIZE,
                        `${EzSpinner.ezApiName}_${spinnerContainerId}`);

                    // Remove the spinner html
                    EzUx.removeElement(spinnerContainerId);

                    delete EzSpinner.ezInstance.activeSpinners[activeSpinnerInfo.spinnerId];

                    EzSpinner.ezInstance.startWaitCount[activeSpinnerInfo.spinnerId] = 0;
                }
            }
        }

        EzSpinner.ezInstance.pageWaitCount = 0;

        return {
            activeSpinners: EzObject.assignOrEmpty(EzSpinner.ezInstance.activeSpinners),
            pageWaitCount: EzNumber.numberOrZero(EzSpinner.ezInstance.pageWaitCount),
            startWaitCount: EzSpinner.ezInstance.startWaitCount
        };
    }

    /**
     * @public @method
     * Injects a spinner into the UX
     *
     * @param {Object|null} options
     */
    ezStart(options = EzSpinner.DEFAULT_OPTIONS) {
        EzSpinner.ezInstance.ezRegisterActiveSpinner(
            EzSpinner.ezInstance.ezSanitizeOptions(
                EzObject.assignOrDefault(
                    options,
                    EzSpinner.DEFAULT_OPTIONS)));
    }

    /**
     * @public @method
     * Removes an existing spinner from the UX
     *
     * @param {undefined|null|string} spinnerId
     * Default: null
     */
    ezStop(ezSpinnerId = EzSpinner.DEFAULT_OPTIONS.id) {
        EzSpinner.ezInstance.ezUnregisterActiveSpinner(
            EzString.stringWithLengthOrDefault(
                ezSpinnerId,
                EzSpinner.DEFAULT_OPTIONS.id));
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Class Initialization and EzApi Registration Section
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond this section !!
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    /**
     * @static
     * @public @readonly @property
     * Gets the name of this class's singleton instance when registered with globalThis.ezApi.
     * @returns {string}
     */
    static get ezApiName() {
        return 'ezSpinner';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets an object of event names that this class may trigger.
     * @returns {object}
     */
    static get ezEventNames() {
        return {
            onReady: 'ezOn_EzSpinner_Ready'
        };
    }

    /**
     * @static
     * @private @field
     * Stores the singleton instance of this class that was created by and registerd with EzApi.
     * @type {EzSpinner}
     */
    static #ezInstance = globalThis.ezApi?.ezclocker?.[EzSpinner.ezApiName]
        ? globalThis.ezApi.ezclocker[EzSpinner.ezApiName]
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the singleton instance of this class that is registered with EzApi (if available yet)
     * @returns {EzSpinner}
     */
    static get ezInstance() {
        return EzSpinner.#ezInstance;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the singleton instance of of this class that is registered with EzApi.
     * @param {EzSpinner} ezSpinner
     */
    static set ezInstance(ezSpinner) {
        if (null != EzSpinner.#ezInstance) {
            throw new Error('EzSpinner\'s singleton instance is already reigstered with EzApi.');
        }

        EzSpinner.#ezInstance = ezSpinner;
    }

    /**
     * @static
     * @private @field
     * Stores the EzApi registration state for this class.
     * Default value is NULL
     * Class is registerd with EzApi when value is EzRegistrationState.REGISTERED
     * @type {string}
     * A valid enum property value from EzRegistrationState
     */
    static #ezApiRegistrationState = globalThis.ezApi?.ezclocker?.[EzSpinner.ezApiName]
        ? EzRegistrationState.REGISTERED
        : null;
    /**
     * @static
     * @public @property @getter
     * Gets the ezApi registration state of this classes's singleton instance.
     * @returns {string}
     * A valid enum property value from EzRegistrationState
     */
    static get ezApiRegistrationState() {
        return EzSpinner.#ezApiRegistrationState;
    }
    /**
     * @static
     * @public @property @setter
     * Sets the ezApi registration state of this classes's singleton instance.
     * @param {string} ezApiRegistrationState
     * A valid enum property value from EzRegistrationState
     */
    static set ezApiRegistrationState(ezApiRegistrationState) {
        EzSpinner.#ezApiRegistrationState = EzRegistrationState.ezValueOf(ezApiRegistrationState);
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if all required dependencies for this class report ready.
     * In otherwords, the require dependency's singleton instance is created
     * and is registered with and accessable from EzApi with globalThis.ezApi.ezclocker.{dependency_name}.
     * @returns {boolean}
     */
    static get #ezCanRegister() {
        return EzRegistrationState.PENDING === EzSpinner.ezApiRegistrationState &&
            globalThis?.ezApi?.ready && globalThis.ezApi?.ezclocker &&
            globalThis.ezApi.ezclocker?.[EzEventEngine.ezApiName]?.ready;
    }

    /**
     * @static
     * @private @readonly @property
     * Gets if this class's singleton instance is registered with ezApi yet.
     * @returns {boolean}
     */
    static get #ezIsRegistered() {
        return null != EzSpinner.ezInstance &&
            EzRegistrationState.REGISTERED === EzSpinner.ezApiRegistrationState;
    }

    /**
     * @static
     * @private @method
     * Attempts to register the singleton instance for this class with globalThis.ezApi. Returns true
     * if successful, false otherwise.
     * @returns {boolean}
     */
    static #ezRegistrator() {
        if (EzSpinner.#ezCanRegister && !EzSpinner.#ezIsRegistered) {
            globalThis.ezApi.ezRegisterNewApi(EzSpinner, EzSpinner.ezApiName);
        }

        return EzSpinner.#ezIsRegistered;
    }

    /**
     * @static
     * Static Initialization
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * >> IMPORTANT <<
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Make sure the following properties and methods get defined BEFORE this static initialization section:
     *     1) Property getter EzSpinner.ezApiName
     *     2) Property getter EzSpinner.ezEventNames
     *     3) Property getter EzSpinner.ezInstance
     *     4) Property setter EzSpinner.ezInstance
     *     5) Property getter EzSpinner.ezApiRegistrationState
     *     6) Property setter EzSpinner.ezApiRegistrationState
     *     7) Property getter EzSpinner.#ezCanRegister
     *     8) Property getter EzSpinner.#ezIsRegistered
     *     9) Method EzSpinner.#ezRegistrator()
     */
    static {
        if (!EzSpinner.#ezIsRegistered) {
            EzSpinner.ezApiRegistrationState = EzRegistrationState.PENDING;

            if (!EzSpinner.#ezRegistrator()) {
                EzClass.ezWaitReady(
                    EzSpinner.ezOnEzApiReadyEventName,
                    EzSpinner.#ezRegistrator);

                EzClass.ezWaitReady(
                    EzEventEngine.ezEventNames.onReady,
                    EzSpinner.#ezRegistrator);
            }
        }
    }

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // !! DO NOT PLACE ANY non-initialization fields, getter/setter methods, static methods, or class methods beyond above section !!
    // NO CODE AFTER THIS SECTION
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
}
